import { skipToken } from '@reduxjs/toolkit/query'
import { toast } from 'core/components/common/Toast'
import { ROUTE_PATHS } from 'core/constants/routePath'
import { IPromptViolation, PromptForm } from 'core/interfaces/Prompt'
import {
  useCreateUpdatePromptMutation,
  useGetPromptQuery,
} from 'lauria/api/prompt'
import { useModelesLLM } from 'lauria/contexts/ModelesLLMContext'
import { useLayoutApplication } from 'lauria/hooks/useLayoutApplication'
import { usePromptDocumentManagement } from 'lauria/hooks/usePromptDocumentManagement'
import { PromptLayoutRootState } from 'lauria/store/slices/promptLayout'
import { MutableRefObject, useEffect, useRef } from 'react'
import { useController, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { generatePath, useNavigate } from 'react-router'
import { useParams } from 'react-router-dom'

export const useCreatePromptForm = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { idPrompt } = useParams()
  const { documents, setDocuments, newFiles, setNewFiles } =
    usePromptDocumentManagement()
  const { modeles, isLoading: isLoadingModeles } = useModelesLLM()
  const currentApplication = useSelector(
    (state: PromptLayoutRootState) => state.promptLayout.application
  )
  const promptId = idPrompt ? parseInt(idPrompt, 10) : undefined
  const {
    data: prompt,
    isLoading: isLoadingPrompt,
    refetch,
    isFetching,
  } = useGetPromptQuery(promptId ? { promptId } : skipToken)
  const initialValues = prompt
    ? {
        ...prompt,
        format: prompt.format
          ? (JSON.parse(prompt.format)?.outputFile ?? null)
          : null,
      }
    : {
        nomPrompt: currentApplication?.nomApplication || '',
        idPrompt: '',
        prompt: '',
        actif: false,
        formApplication: '',
        modele: '',
        temperature: 0.5,
        webSearch: false,
        format: null,
      }
  const {
    handleSubmit,
    setError,
    formState: { errors },
    control,
    reset,
    getValues,
    setValue,
    register,
    watch,
  } = useForm<PromptForm>({
    defaultValues: initialValues,
  })
  const { refetch: refetchApplication } = useLayoutApplication()
  useEffect(() => {
    if (modeles && modeles.length > 0 && !getValues('modele')) {
      const defaultModele = modeles.find((modele) => modele.defaut === true)
      if (defaultModele) {
        setValue('modele', defaultModele.nom)
      }
    }
  }, [modeles, getValues, setValue])
  useEffect(() => {
    if (prompt) {
      reset({
        idApplication: prompt.idApplication,
        nomPrompt: prompt.nomPrompt,
        prompt: prompt.prompt,
        actif: prompt.actif,
        temperature: prompt.temperature,
        modele: prompt.modele,
        format: prompt.format
          ? (JSON.parse(prompt.format)?.outputFile ?? null)
          : null,
        webSearch: prompt.webSearch ?? false,
      })
      setDocuments(prompt.documents || [])
    }
  }, [prompt, reset, setDocuments])

  const cancel = (event: any) => {
    event.preventDefault()
    navigate(
      generatePath(ROUTE_PATHS.PROMPTS, {
        idApplication: currentApplication?.id,
      })
    )
  }

  const handleSubmitForm = (data: any) => {
    onSubmit(data, true)
  }

  const promptRef = useRef<HTMLTextAreaElement | null>(null)
  const fields = {
    nomField: useController({
      name: 'nomPrompt',
      control,
      rules: { required: true },
    }).field,
    promptField: useController({
      name: 'prompt',
      control,
      rules: { required: true },
    }).field,
    temperatureField: useController({
      name: 'temperature',
      control,
      rules: { required: false },
    }).field,
    modeleField: useController({
      name: 'modele',
      control,
      rules: { required: true },
    }).field,
    formatField: useController({
      name: 'format',
      control,
      rules: { required: false },
    }).field,
    webSearchField: useController({
      name: 'webSearch',
      control,
      rules: { required: false },
    }).field,
  }
  const [createUpdatePrompt, { isLoading }] = useCreateUpdatePromptMutation()

  const addTextToTextarea = (
    event: any,
    text: string,
    ref: MutableRefObject<any>,
    fieldName: any
  ) => {
    event.preventDefault()
    const textarea = ref.current
    if (textarea) {
      const { selectionStart, selectionEnd, value } = textarea
      const newValue =
        selectionStart || selectionStart === 0
          ? value.substring(0, selectionStart) +
            text +
            value.substring(selectionEnd)
          : value + text
      setValue(fieldName, newValue)
    }
  }

  const onSubmit = (data: any, actif = false, redirectManage = false) => {
    const formData = new FormData()
    formData.append('nomPrompt', data.nomPrompt)
    formData.append('prompt', data.prompt)
    formData.append('temperature', data.temperature)
    formData.append('modele', data.modele)
    formData.append('webSearch', data.webSearch.toString())
    formData.append(
      'format',
      JSON.stringify(data.format === null ? null : { outputFile: data.format })
    )
    if (currentApplication?.id) {
      formData.append('idApplication', currentApplication?.id.toString())
    }
    const docIds =
      documents
        .filter((doc) => doc.id !== undefined)
        .map((doc) => doc.id?.toString()) ?? []
    formData.append('documents', JSON.stringify(docIds))
    const documentNames = documents.map((doc) => doc.nomDocument)
    if (newFiles.length === 0) {
      formData.append('files', JSON.stringify([]))
    } else {
      newFiles.forEach((file) => {
        if (documentNames.length === 0 || !documentNames.includes(file.name)) {
          formData.append('files', JSON.stringify([]))
        } else {
          formData.append('files[]', file)
        }
      })
    }
    if (promptId) {
      formData.append('idPrompt', promptId.toString())
      createUpdatePrompt({ data: formData })
        .unwrap()
        .then((prompt) => {
          if (prompt.id) {
            navigate(
              generatePath(ROUTE_PATHS.PROMPTS, {
                idApplication: currentApplication?.id,
              })
            )
            refetch()
            refetchApplication()
            toast.success(t('prompts.form.successUpdate'))
          }
        })
        .catch((error) => {
          if (typeof error.data.violations !== 'undefined') {
            const violations: IPromptViolation[] = error.data.violations

            for (let violation of violations) {
              if (violation.propertyPath === 'idApplication') {
                navigate(ROUTE_PATHS.ERROR)
              } else {
                setError(violation.propertyPath, {
                  type: 'custom',
                  // @todo : mettre en place des code de traductions
                  message: violation.message,
                })
              }
            }
          }
        })
    } else {
      // creation
      formData.append('actif', actif.toString())
      formData.append('distribution', '0')
      createUpdatePrompt({ data: formData })
        .unwrap()
        .then((prompt) => {
          if (prompt.id) {
            if (redirectManage) {
              navigate(
                generatePath(ROUTE_PATHS.PROMPTS, {
                  idApplication: currentApplication?.id,
                })
              )
            } else {
              navigate(
                generatePath(ROUTE_PATHS.PROMPTS, {
                  idApplication: currentApplication?.id,
                })
              )
            }
          }
          refetchApplication()
          toast.success(t('prompts.form.success'))
        })
        .catch((error) => {
          if (typeof error.data.violations !== 'undefined') {
            const violations: IPromptViolation[] = error.data.violations

            for (let violation of violations) {
              if (violation.propertyPath === 'idApplication') {
                navigate(ROUTE_PATHS.ERROR)
              } else {
                setError(violation.propertyPath, {
                  type: 'custom',
                  // @todo : mettre en place des code de traductions
                  message: violation.message,
                })
              }
            }
          }
        })
    }
  }

  const onFormSubmit = handleSubmit(handleSubmitForm)

  const state = {
    isLoading: isLoading || isLoadingPrompt || isFetching || isLoadingModeles,
    documents,
    newFiles,
  }

  const setters = {
    setDocuments,
    setNewFiles,
  }

  return {
    state,
    setters,
    fields,
    promptRef,
    addTextToTextarea,
    errors,
    onFormSubmit,
    cancel,
    setValue,
    register,
    watch,
  }
}
